import React from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Download, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Privacy toggles + GDPR export/erasure controls (extracted from
// Onboarding.jsx in the Phase 15 component split — behaviour unchanged).
export function PrivacySection({
  type,
  showRealName, setShowRealName,
  showOnLeaderboard, setShowOnLeaderboard,
  profilePrivate, setProfilePrivate,
  emailOptOut, setEmailOptOut,
}) {
  const nav = useNavigate();
  const { logout } = useAuth();
  return (
    <div className="pt-4 border-t border-neutral-100">
      <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Privacy</label>
      {type !== "organisation" && (
        <div className="flex items-center justify-between gap-4 mt-3">
          <div>
            <div className="text-sm text-neutral-700">Show my real name on my profile</div>
            <div className="text-xs text-neutral-500">Off by default. When on, visitors to your profile see your real name alongside your display name.</div>
          </div>
          <Switch data-testid="privacy-real-name-toggle" checked={showRealName} onCheckedChange={setShowRealName} />
        </div>
      )}
      <div className="flex items-center justify-between gap-4 mt-3">
        <div>
          <div className="text-sm text-neutral-700">Show me on leaderboards</div>
          <div className="text-xs text-neutral-500">Only your first name and city are ever shown — never your email or postcode.</div>
        </div>
        <Switch data-testid="privacy-leaderboard-toggle" checked={showOnLeaderboard} onCheckedChange={setShowOnLeaderboard} />
      </div>
      <div className="flex items-center justify-between gap-4 mt-4">
        <div>
          <div className="text-sm text-neutral-700">Private profile</div>
          <div className="text-xs text-neutral-500">Hide your public profile page, statistics and location from other users.</div>
        </div>
        <Switch data-testid="privacy-profile-toggle" checked={profilePrivate} onCheckedChange={setProfilePrivate} />
      </div>
      <div className="flex items-center justify-between gap-4 mt-4">
        <div>
          <div className="text-sm text-neutral-700">Email notifications</div>
          <div className="text-xs text-neutral-500">Local alerts, messages and digests by email. In-app notifications are unaffected.</div>
        </div>
        <Switch data-testid="privacy-email-toggle" checked={!emailOptOut} onCheckedChange={(v) => setEmailOptOut(!v)} />
      </div>
      <div className="mt-5 pt-4 border-t border-neutral-100">
        <div className="text-xs text-neutral-500 mb-3">
          Your data, your rules — see our <a href="/privacy" className="text-forest underline">privacy notice</a>.
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="export-data-btn"
            onClick={async () => {
              try {
                const data = await api.exportData();
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = "civichearth-my-data.json";
                a.click();
                URL.revokeObjectURL(a.href);
                toast.success("Your data has been downloaded");
              } catch { toast.error("Export failed"); }
            }}
            className="text-xs px-3 py-2 rounded-full border border-neutral-200 text-neutral-600 hover:border-forest hover:text-forest inline-flex items-center gap-1.5 transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Download my data
          </button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="delete-account-btn" className="text-xs px-3 py-2 rounded-full border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1.5 transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Delete my account
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="delete-account-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your account permanently?</AlertDialogTitle>
                <AlertDialogDescription>
                  Your profile, applications, swipes, stories and notifications are deleted. Hour logs and messages other people rely on are anonymised to "Deleted user". This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep my account</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="delete-account-confirm"
                  onClick={async () => {
                    try {
                      await api.deleteAccount();
                      toast("Your account and data have been deleted. Goodbye!");
                      await logout();
                      nav("/");
                    } catch { toast.error("Deletion failed — please try again"); }
                  }}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete everything
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}
